import React, { useCallback, useEffect, useState } from 'react';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../components/bootstrap/Card';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Button from '../../components/bootstrap/Button';
import UserStatus from './UserStatus';
import { toast } from 'react-toastify';
import Label from '../../components/bootstrap/forms/Label';
import Select from '../../components/bootstrap/forms/Select';
import { useFormik } from 'formik';
import fNameError, { designationError, emailErrors, employeeIdError, lNameError, passwordErrors, phoneErrors } from './UserValidation';
import { Department, Role, User } from '../Role_Layout/roleTypes';
import { useNavigate, useParams } from 'react-router-dom';
import { centralApi } from '../AxiosInstance/CreateInstance';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import { SpinnerCircular } from 'spinners-react';
import Page from '../../layout/Page/Page';


export interface ApiResponse<T> {
	status: number;
	msg: string;
	data: T;
}

const UserCreate = () => {
	const [role, setRoles] = useState<Role[]>([]);
	const [department, setDepartment] = useState<Department[]>([]);
	const [manager, setManager] = useState<User[]>([]);
	const [updateUser, setUpdateUser] = useState<User | null>(null);
	const [showPassword, setShowPassword] = useState(false);
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(false);
	const [dataReady, setDataReady] = useState(false);
	const { id } = useParams();
	const navigate = useNavigate();
	const isUpdate = !!id;

	const generatePassword = (length = 12) => {
		if (length < 8 || length > 16) {
			throw new Error("Password length must be between 8 and 16 characters");
		}

		const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
		const lower = "abcdefghijklmnopqrstuvwxyz";
		const number = "0123456789";
		const special = "!@#$%^&*()-_=+";
		const all = upper + lower + number + special;

		const getRandom = (chars: string) => chars[Math.floor(Math.random() * chars.length)];

		let password = [
			getRandom(upper),
			getRandom(lower),
			getRandom(number),
			getRandom(special),
		];

		for (let i = 4; i < length; i += 1) {
			password.push(getRandom(all));
		}

		password = password.sort(() => 0.5 - Math.random());

		return password.join('');
	};

	const fetchUsers = useCallback(async () => {
		try {
			let allUsers: User[] = [];
			let pageNo = 1;
			const limit = 100;
			let hasMore = true;

			while (hasMore) {
				// eslint-disable-next-line no-await-in-loop
				const res = await centralApi.get(
					`userService/user/list?pageNo=${pageNo}&limit=${limit}`,
				);
				const currentPage = res.data.data;

				if (currentPage.length === 0) {
					hasMore = false;
				} else {
					allUsers = [...allUsers, ...currentPage];
					pageNo += 1;
					hasMore = currentPage.length === limit;
				}
			}

			setUsers(allUsers);
			if (id) {
				const user = allUsers.find((item: User) => item.id === id);
				setUpdateUser(user || null);
			}
		} catch (error) {
			toast.error('Error fetching user data', { closeOnClick: true });
		}
	}, [id]);

	useEffect(() => {
		const fetchRoles = async () => {
			try {
				const res = await centralApi.get(`userService/role/list?limit=${100}`);
				setRoles(res.data.data);
			} catch (error) {
				toast.error('Error fetching roles');
			}
		};

		const fetchDepartment = async () => {
			try {
				const res = await centralApi.get(`userService/department/list?limit=${100}`);
				setDepartment(res.data.data);
			} catch (error) {
				toast.error('Error fetching departments');
			}
		};

		const loadAllData = async () => {
			setLoading(true);
			await Promise.all([fetchUsers(), fetchRoles(), fetchDepartment()]);
			setLoading(false);
			setDataReady(true);
		};

		loadAllData();
	}, [fetchUsers]);

	const handleDepartment = async (value: string) => {
		formik.setFieldValue('department', value);

		const selectDepartment = department.find((item) => item.name === value);
		if (selectDepartment) {
			try {
				const res = await centralApi.get(
					`userService/user/filter-list?departmentId=${selectDepartment._id}`,
				);
				// const managers = res.data.data;
				const managers = res.data.data.filter(
					(item: User) => item.id !== updateUser?.id
				);
				setManager(managers);

				if (updateUser?.reportingManager && managers.length > 0) {
					const found = managers.find(
						(item: User) =>
							`${item.fName} ${item.lName}` === updateUser.reportingManager,
					);

					if (found) {
						formik.setFieldValue('reportingManager', found.fName);
					}
				}
			} catch (error) {
				toast.error('Error fetching managers');
			}
		}
	};

	useEffect(() => {
		if (
			updateUser &&
			updateUser.department &&
			department.length > 0 &&
			users.length > 0 &&
			!formik.values.reportingManager
		) {
			handleDepartment(updateUser.department);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateUser, department, users]);

	useEffect(() => {
		if (updateUser && updateUser.department) {
			handleDepartment(updateUser.department);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateUser]);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			employeeId: updateUser?.employeeId || '',
			fName: updateUser?.fName || '',
			lName: updateUser?.lName || '',
			email: updateUser?.email || '',
			phone: updateUser?.phone || '',
			designation: updateUser?.designation || '',
			role: updateUser?.role || '',
			department: updateUser?.department || '',
			reportingManager: updateUser?.reportingManager || '',
			password: '',
			status: updateUser?.status !== 'active' ? 'Offline' : 'Online',
		},
		validate: (values) => {
			const errors: {
				employeeId?: string;
				fName?: string;
				lName?: string;
				email?: string;
				phone?: string;
				password?: string;
				designation?: string;
				role?: string;
				department?: string;
				reportingManager?: string;
				status?: string;
			} = {};
			if (employeeIdError(values.employeeId, users, updateUser?.id)) {
				errors.employeeId = employeeIdError(values.employeeId, users, updateUser?.id);
			}
			if (fNameError(values.fName)) {
				errors.fName = fNameError(values.fName);
			}
			if (lNameError(values.lName)) {
				errors.lName = lNameError(values.lName);
			}
			if (designationError(values.designation)) {
				errors.designation = designationError(values.designation);
			}
			if (emailErrors(values.email)) {
				errors.email = emailErrors(values.email);
			}
			if (phoneErrors(values.phone)) {
				errors.phone = phoneErrors(values.phone);
			}
			if (!values.role) {
				errors.role = 'Role is required';
			}
			if (!values.department) {
				errors.department = 'Department is required';
			}


			if (!values.status && isUpdate) {
				errors.status = 'Status is required';
			}

			if (isUpdate && updateUser) {
				if (users.some((item) => item.phone === values.phone && item.id !== id)) {
					errors.phone = 'Phone number already exists';
				}
				if (users.some((item) => item.email === values.email && item.id !== id)) {
					errors.email = 'Email already exists';
				}
			} else {
				if (users.some((item) => item.phone === values.phone)) {
					errors.phone = 'Phone number already exists';
				}
				if (users.some((item) => item.email === values.email)) {
					errors.email = 'Email already exists';
				}
				if (passwordErrors(values.password)) {
					errors.password = passwordErrors(values.password);
				}
			}

			return errors;
		},
		validateOnChange: true,
		validateOnBlur: true,
		onSubmit: async (values) => {
			if (Object.keys(formik.errors).length > 0) {
				toast.error('Please fill all the required fields', { closeOnClick: true });
				return;
			}

			const getRole = role.find((item: Role) => item.name === values.role);
			const getDepartment = department.find((item) => item.name === values.department);
			const getManager = manager.find((item: User) => item.fName === values.reportingManager);

			const payload = {
				employeeId: values.employeeId,
				fName: values.fName,
				lName: values.lName,
				email: values.email,
				phone: values.phone,
				designation: values.designation,
				roleId: getRole ? getRole._id : '',
				departmentId: getDepartment ? getDepartment._id : '',
				reportingManagerId: getManager ? getManager.id : '',
				status: 'active',
				emailVerify: true,
				phoneVerify: true,
				...(isUpdate ? {} : { password: values.password }),
			};

			try {
				if (isUpdate) {
					await centralApi.patch(`userService/user/update`, {
						userId: id,
						employeeId: values.employeeId,
						fName: values.fName,
						lName: values.lName,
						email: values.email,
						phone: values.phone,
						designation: values.designation,
						roleId: getRole ? getRole._id : '',
						departmentId: getDepartment ? getDepartment._id : '',
						reportingManagerId: getManager ? getManager.id : '',
						status: values.status === 'Online' ? 'active' : 'inactive',
						emailVerify: true,
						phoneVerify: true,
					});
					toast.success('User updated successfully', { closeOnClick: true });
				} else {
					await centralApi.post('userService/user/register', payload);
					toast.success('User created successfully', { closeOnClick: true });
				}
				navigate('/user-list');
				formik.resetForm();
			} catch (error) {
				toast.error(`Error ${isUpdate ? 'updating' : 'creating'} user`, {
					closeOnClick: true,
				});
			}
		},
	});

	function capitalizeWords(str: string) {
		if (!str) return '';
		return str
			.toLowerCase()
			.split(' ')
			.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	return (
		<Page container='fluid'>
			<Card stretch className="d-flex flex-column ">
				<CardHeader borderSize={1}>
					<CardLabel icon='personAdd' iconColor='info' className='ms-4'>
						<CardTitle tag='div' className='h5'>
							{isUpdate ? 'Update User' : 'Create User'}
						</CardTitle>
					</CardLabel>
				</CardHeader>

				<CardBody className="d-flex flex-column flex-grow-1">
					{(() => {
						if (!dataReady) {
							return (
								<div
									className='d-flex w-100 flex-column justify-content-center align-items-center'
									style={{ minHeight: '100%', height: '100%' }}>
									<SpinnerCircular
										enabled={loading}
										color='#45b7d1'
										thickness={100}
										size={50}
										secondaryColor=''
									/>
									<p className='mt-2'>Loading...</p>
								</div>
							);
						}
						return (
							<form onSubmit={formik.handleSubmit} className="d-flex flex-column flex-grow-1">
								<div className="flex-grow-1">
									<div className='row mb-4'>
										<div className='col'>
											<FormGroup
												id='employeeId'
												label='Employee ID'
												className='mb-3'>
												<Input
													id='employeeId'
													autoComplete='off'
													className='form-control py-2'
													placeholder='Enter Employee ID'
													value={formik.values.employeeId || ''}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													isTouched={!!formik.touched.employeeId}
													invalidFeedback={formik.errors.employeeId}
													isValid={formik.isValid && !formik.errors.employeeId}
													name='employeeId'
												/>
											</FormGroup>
										</div>
										<div className='col'>
											<FormGroup
												id='fName'
												label='First Name'
												className='mb-3'>
												<Input
													id='fName'
													autoComplete='off'
													className='form-control py-2'
													placeholder='Enter First Name'
													value={capitalizeWords(formik.values.fName)}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													isTouched={!!formik.touched.fName}
													invalidFeedback={formik.errors.fName}
													isValid={formik.isValid && !formik.errors.fName}
													name='fName'
												/>
											</FormGroup>
										</div>
									</div>
									<div className='row mb-4'>
										<div className='col'>
											<FormGroup
												id='lName'
												label='Last Name'
												className='mb-3'>
												<Input
													type='text'
													id='lName'
													autoComplete='off'
													className='form-control py-2'
													placeholder='Enter Last Name'
													value={capitalizeWords(formik.values.lName)}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													isTouched={!!formik.touched.lName}
													invalidFeedback={formik.errors.lName}
													isValid={formik.isValid && !formik.errors.lName}
													name='lName'
												/>
											</FormGroup>
										</div>
										<div className='col'>
											<FormGroup
												id='user-email'
												label='Email'
												className='mb-3'>
												<Input
													type='email'
													id='user-email'
													autoComplete='off'
													className='form-control py-2'
													placeholder='Enter Your Email'
													value={formik.values.email}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													isTouched={!!formik.touched.email}
													invalidFeedback={formik.errors.email}
													isValid={formik.isValid && !formik.errors.email}
													name='email'
												/>
											</FormGroup>
										</div>
									</div>

									{!isUpdate && (
										<div className="row mb-4">
											<div className='col'>
												<FormGroup
													id='user-phone'
													label='Phone No.'
													className='mb-3'>
													<Input
														type='text'
														className='form-control py-2'
														placeholder='Enter Mobile Number'
														value={`+91 ${formik.values.phone}`}
														onChange={(
															e: React.ChangeEvent<HTMLInputElement>,
														) => {
															const value = e.target.value.replace(
																/^\+91\s*/,
																'',
															);
															formik.setFieldValue('phone', value);
														}}
														onBlur={formik.handleBlur}
														isTouched={formik.touched.phone}
														invalidFeedback={formik.errors.phone}
														isValid={formik.isValid && !formik.errors.phone}
														name='phone'
													/>
												</FormGroup>
											</div>
											<div className="col">
												<FormGroup id="password" label="Password" className="mb-3">
													<div style={{ position: "relative", marginBottom: "0.5rem" }}>
														{/* Input field */}
														<Input
															id="password"
															type={showPassword ? "text" : "password"}
															placeholder="Enter your password"
															autoComplete="current-password"
															value={formik.values.password || ""}
															isTouched={!!formik.touched.password}
															isValid={formik.isValid && !formik.errors.password}
															invalidFeedback={formik.errors.password}
															onChange={formik.handleChange}
															onBlur={formik.handleBlur}
															style={{
																width: "100%",
																paddingRight: "5rem",
															}}
														/>

														{/* Refresh button */}
														<button
															type="button"
															title="Auto-generate password"
															style={{
																position: "absolute",
																right: "4.5rem",
																top: formik.touched.password && formik.errors.password ? "30%" : "50%",
																transform: "translateY(-50%)",
																background: "transparent",
																borderRadius: "50%",
																width: "30px",
																height: "30px",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																cursor: "pointer",
																border: "none",
																padding: 0,
															}}
															onClick={() => {
																const pwd = generatePassword();
																formik.setFieldValue("password", pwd);
																setShowPassword(true);
															}}
														>
															<RefreshCw size={16} className="text-blue" />
														</button>

														{/* Eye toggle button */}
														<button
															type="button"
															onClick={() => setShowPassword((prev) => !prev)}
															style={{
																position: "absolute",
																right: "2rem",
																top: formik.touched.password && formik.errors.password ? "30%" : "50%",
																transform: "translateY(-50%)",
																background: "none",
																border: "none",
																cursor: "pointer",
															}}
														>
															{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
														</button>

													</div>
												</FormGroup>
											</div>
										</div>
									)}

									<div className='row mb-4'>
										<div className='col'>
											<FormGroup>
												<Label htmlFor='user-role'>Role</Label>
												<Select
													ariaLabel='user-role'
													placeholder='Select User Role'
													list={role?.map((item) => ({
														value: item.name,
														text: capitalizeWords(item.name),
													}))}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													isTouched={!!formik.touched.role}
													invalidFeedback={formik.errors.role}
													isValid={formik.isValid && !formik.errors.role}
													name='role'
													value={formik.values.role}
												/>
											</FormGroup>
										</div>
										<div className='col'>
											<FormGroup>
												<Label htmlFor='department'>Department</Label>
												<Select
													ariaLabel='user-department'
													placeholder='Select Department'
													list={department?.map((item) => ({
														value: item.name,
														text: item.name,
													}))}
													onChange={(
														e: React.ChangeEvent<HTMLSelectElement>,
													) => handleDepartment(e.target.value)}
													onBlur={formik.handleBlur}
													isTouched={!!formik.touched.department}
													invalidFeedback={formik.errors.department}
													isValid={
														formik.isValid && !formik.errors.department
													}
													name='department'
													value={formik.values.department}
												/>
											</FormGroup>
										</div>

									</div>
									<div className="row mb-4">
										<div className='col'>
											<FormGroup
												id='designation'
												label='Designation'
												className='mb-3'>
												<Input
													id='designation'
													autoComplete='off'
													className='form-control py-2'
													placeholder='Enter Designation'
													value={formik.values.designation}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													isTouched={!!formik.touched.designation}
													invalidFeedback={formik.errors.designation}
													isValid={formik.isValid && !formik.errors.designation}
													name='designation'
												/>
											</FormGroup>
										</div>
										<div className="col">
											<div className='col'>
												<FormGroup>
													<Label htmlFor='reportingManager'>Reporting Manager</Label>
													<Select
														ariaLabel='user-manager'
														placeholder='Select Manager'
														list={
															manager && manager.length > 0
																? manager?.map((item: User) => ({
																	value: item.fName,
																	text: capitalizeWords(`${item.fName} ${item.lName}`),
																}))
																: [
																	{
																		value: '',
																		text: 'No Manager Found',
																	},
																]
														}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														isTouched={
															!!formik.touched.reportingManager &&
															!!formik.values.reportingManager
														}
														invalidFeedback={formik.errors.reportingManager}
														isValid={formik.isValid && !formik.errors.reportingManager}
														name='reportingManager'
														value={formik.values.reportingManager}
													/>
												</FormGroup>
											</div>
										</div>
									</div>

									{isUpdate && (
										<div className='row'>
											<div className='col'>
												<FormGroup>
													<UserStatus
														status={
															formik.values.status as
															| 'Online'
															| 'Offline'
														}
														setStatus={(value) =>
															formik.setFieldValue('status', value)
														}
													/>
												</FormGroup>
											</div>
										</div>
									)}
								</div>
								<div className="mt-auto pt-4">
									<div className="d-flex justify-content-end gap-2">
										<Button
											className="hrms-btn"
											color="dark"
											onClick={() => navigate('/user-list')}>
											Cancel
										</Button>

										<Button
											className="hrms-btn hrms-gradient-btn"
											type="submit">
											{isUpdate ? 'Update' : 'Save'}
										</Button>
									</div>
								</div>
							</form>
						);
					})()}
				</CardBody>
			</Card>
		</Page >
	);
};

export default UserCreate;
